import { useApolloClient } from '@apollo/client';
import { Button, Form, Space, message } from 'antd';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MenuItemSpecsFragment } from '../../../../api/fragments/MenuItemSpecs';
import { type RouterSpecsFragment } from '../../../../api/fragments/RouterSpecs';
import {
    CreateRouterMenuDocument,
    type CreateRouterMenuMutation,
    type CreateRouterMenuMutationVariables,
} from '../../../../api/mutations/createRouterMenu';
import { type MenuItemSettings } from '../../../../api/types';
import DrawerWithAutoWidth from '../../../../components/DrawerWithAutoWidth';
import useHandleError from '../../../../utilities/useHandleError';
import MenuItemGroup from './menuItems';

export type RouterMenuDrawerProps = {
    onClose: () => void;
    visible: boolean;
    router: RouterSpecsFragment;
    menuItems: MenuItemSpecsFragment[];
};

type RouterMenuItemFormValue = MenuItemSettings;

const RouterMenuDrawer = ({ visible, router, onClose, menuItems }: RouterMenuDrawerProps) => {
    const { t } = useTranslation('routerDetails');

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<RouterMenuItemFormValue>(async values => {
        message.loading({
            content: t('routerDetails:menuItems.message.updatingMenuItems'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient.mutate<CreateRouterMenuMutation, CreateRouterMenuMutationVariables>({
            mutation: CreateRouterMenuDocument,
            variables: {
                routerId: router.id,
                menuItem: values,
            },
        });

        message.success({
            content: t('routerDetails:menuItems.message.successUpdatedMenuItems'),
            key: 'primary',
        });

        // close drawer after creation
        onClose();
    }, []);

    const initialValues: RouterMenuItemFormValue = {};

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('routerDetails:menuItems.menuItemDrawer.cancelButton')}</Button>
                    <Button form="createRouterMenuForm" htmlType="submit" type="primary">
                        {t('routerDetails:menuItems.menuItemDrawer.submitButton')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t('routerDetails:menuItems.menuItemDrawer.title')}
            visible={visible}
            destroyOnClose
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form id="createRouterMenuForm" layout="vertical" onSubmitCapture={handleSubmit}>
                        <MenuItemGroup menuItems={menuItems} router={router} />
                    </Form>
                )}
            </Formik>
        </DrawerWithAutoWidth>
    );
};

export const useRouterMenuDrawer = (menuItems: MenuItemSpecsFragment[]) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (router: RouterMenuDrawerProps['router']) => (
            <RouterMenuDrawer menuItems={menuItems} onClose={actions.close} router={router} visible={visible} />
        ),
    };
};

export default RouterMenuDrawer;
